import { scopedStorageKey } from "@/lib/user-scope";

const WALLET_APPEARANCE_KEY = "genrl.wallet.appearance.v1";
const WALLET_DASHBOARD_SNAPSHOT_KEY = "genrl.wallet.dashboard-snapshot.v1";
const WALLET_DASHBOARD_SNAPSHOT_MAX_AGE_MS = 6 * 60 * 60 * 1000;
export const WALLET_BACKGROUND_MAX_BYTES = 3 * 1024 * 1024;

export type WalletNotificationSettings = {
  workload: boolean;
  billing: boolean;
  team: boolean;
};

export type WalletAppearance = {
  background: string;
  lightText: boolean;
  customImage: string;
  notifications: WalletNotificationSettings;
  updatedAt: string;
};

const DEFAULT_WALLET_NOTIFICATIONS: WalletNotificationSettings = {
  workload: true,
  billing: true,
  team: false,
};

type WalletDashboardSnapshotEnvelope<T> = {
  version: 1;
  updatedAt: string;
  payload: T;
};

function storageGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Appearance and dashboard snapshots are best-effort; live backend data remains authoritative.
  }
}

export function walletBackgroundCSSImage(dataURL: string): string {
  const safe = String(dataURL || "").replace(/["\\]/g, "\\$&");
  return safe ? `url("${safe}")` : "";
}

export function extractWalletBackgroundImage(background: string): string {
  const raw = String(background || "").trim();
  const match = raw.match(/^url\((['"]?)(.*)\1\)$/);
  const value = String(match?.[2] || "").trim();
  return value.startsWith("data:image/") ? value : "";
}

function normalizeWalletNotifications(value: unknown): WalletNotificationSettings {
  const parsed = (value || {}) as Partial<Record<keyof WalletNotificationSettings, unknown>>;
  return {
    workload: typeof parsed.workload === "boolean" ? parsed.workload : DEFAULT_WALLET_NOTIFICATIONS.workload,
    billing: typeof parsed.billing === "boolean" ? parsed.billing : DEFAULT_WALLET_NOTIFICATIONS.billing,
    team: typeof parsed.team === "boolean" ? parsed.team : DEFAULT_WALLET_NOTIFICATIONS.team,
  };
}

export function readWalletAppearance(defaultBackground: string, userID?: string): WalletAppearance {
  const fallback: WalletAppearance = {
    background: defaultBackground,
    lightText: false,
    customImage: "",
    notifications: { ...DEFAULT_WALLET_NOTIFICATIONS },
    updatedAt: "",
  };
  const raw = storageGet(scopedStorageKey(WALLET_APPEARANCE_KEY, userID));
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as Partial<WalletAppearance>;
    const customImage = String(parsed.customImage || "").trim();
    const background = customImage
      ? walletBackgroundCSSImage(customImage)
      : String(parsed.background || defaultBackground).trim() || defaultBackground;
    return {
      background,
      lightText: parsed.lightText === true,
      customImage,
      notifications: normalizeWalletNotifications(parsed.notifications),
      updatedAt: String(parsed.updatedAt || ""),
    };
  } catch {
    return fallback;
  }
}

function writeWalletAppearancePayload(payload: WalletAppearance, userID?: string) {
  storageSet(scopedStorageKey(WALLET_APPEARANCE_KEY, userID), JSON.stringify(payload));
}

export function writeWalletAppearance(
  appearance: Pick<WalletAppearance, "background" | "lightText">,
  userID?: string,
) {
  const existing = readWalletAppearance("", userID);
  const background = String(appearance.background || "").trim();
  const customImage = extractWalletBackgroundImage(background);
  const payload: WalletAppearance = {
    background: customImage ? "" : background,
    lightText: appearance.lightText === true,
    customImage,
    notifications: existing.notifications,
    updatedAt: new Date().toISOString(),
  };
  writeWalletAppearancePayload(payload, userID);
}

export function writeWalletNotificationSettings(
  notifications: Partial<WalletNotificationSettings>,
  userID?: string,
) {
  const existing = readWalletAppearance("", userID);
  writeWalletAppearancePayload({
    background: existing.customImage ? "" : existing.background,
    lightText: existing.lightText,
    customImage: existing.customImage,
    notifications: normalizeWalletNotifications({
      ...existing.notifications,
      ...notifications,
    }),
    updatedAt: new Date().toISOString(),
  }, userID);
}

export function readWalletDashboardSnapshot<T>(
  userID?: string,
  maxAgeMs = WALLET_DASHBOARD_SNAPSHOT_MAX_AGE_MS,
): T | null {
  const raw = storageGet(scopedStorageKey(WALLET_DASHBOARD_SNAPSHOT_KEY, userID));
  if (!raw) return null;
  try {
    const envelope = JSON.parse(raw) as Partial<WalletDashboardSnapshotEnvelope<T>>;
    if (envelope.version !== 1 || !envelope.payload) return null;
    const updatedAt = new Date(String(envelope.updatedAt || "")).getTime();
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > maxAgeMs) return null;
    return envelope.payload;
  } catch {
    return null;
  }
}

export function writeWalletDashboardSnapshot<T>(payload: T, userID?: string) {
  const envelope: WalletDashboardSnapshotEnvelope<T> = {
    version: 1,
    updatedAt: new Date().toISOString(),
    payload,
  };
  storageSet(scopedStorageKey(WALLET_DASHBOARD_SNAPSHOT_KEY, userID), JSON.stringify(envelope));
}

export function readWalletBackgroundFile(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    return Promise.reject(new Error("Choose an image file."));
  }
  if (file.size > WALLET_BACKGROUND_MAX_BYTES) {
    return Promise.reject(new Error("Choose an image under 3 MB so it can persist on this device."));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that image."));
    reader.onload = () => {
      const result = String(reader.result || "");
      if (!result.startsWith("data:image/")) {
        reject(new Error("Could not read that image."));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(file);
  });
}
